import { ChangeEvent, useState } from "react";

import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import { IUserState } from "../../redux/user/userSlice";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";

import { useNavigate, useSearchParams } from "react-router-dom";
import backendApi from "../../config/backendApi";
import BrokenRefReport, { IBrokenRefReport } from "../../components/BrokenRefReport";


function Main() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { username } = useAppSelector<IUserState>((state: RootState) => state.user);

  const [language] = useState<string>(params.get("language") || "en");
  const [category, setCategory] = useState<string>(params.get("category") || "cs1");
  const [page, setPage] = useState<string>(params.get("page") || "");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [warning, setWarning] = useState<string>("");
  const [report, setReport] = useState<IBrokenRefReport | null>(null);

  const handlePageChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(e.target.value);
  }

  const handleLoadClick = () => {
    if (!page.trim()) return;
    setLoading(true);
    setError("");
    setWarning("");
    setReport(null);
    backendApi.post("/fix", {
        language: language,
        category: category,
        page: page.trim()
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(({ data }) => {
      setReport(data.report || null);
      if (data.no_changes) {
        setWarning("No automatic changes were generated.");
        setLoading(false);
      } else {
        navigate("/fix/" + data["id"]);
      }
    }).catch((err) => {
      setError(err.response?.data?.message || "Failed to load fixes");
      setLoading(false);
    });
  }

  return (
    <Container fixed>
      <Paper elevation={6} sx={{ marginTop: "80px", padding: "30px" }}>
        {username !== "" && username !== null ?
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select value={language} defaultValue="en" label="Language" disabled>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6} sx={{ marginTop: "20px" }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  defaultValue="cs1"
                  label="Category"
                  onChange={(e) => setCategory(e.target.value as string)}
                >
                  <MenuItem value="cs1">CS1 errors: URL</MenuItem>
                  <MenuItem value="missing_reflist">Missing references list</MenuItem>
                  <MenuItem value="broken_ref">Broken reference names</MenuItem>
                  <MenuItem value="duplicate_ref">Duplicate reference names</MenuItem>
                  {/* <MenuItem value="ve_refnames">Auto-generated ref names (VE cleanup)</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6} sx={{ marginTop: "20px" }}>
              <TextField value={page} label="Page name" variant="outlined" 
                onChange={handlePageChange} fullWidth
              />
            </Grid>
            <Grid item xs={6}></Grid>
            <Button
              variant="outlined" size="large"
              sx={{ marginLeft: "16px", marginTop: "20px" }}
              onClick={handleLoadClick}
              disabled={loading || !page.trim()}
            >
              {loading ? <CircularProgress size={24} /> : "Load"}
            </Button>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ marginTop: "16px" }}>{error}</Alert>
              </Grid>
            )}
            {warning && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ marginTop: "16px" }}>{warning}</Alert>
              </Grid>
            )}
            {report && (
              <Grid item xs={12}>
                <BrokenRefReport report={report} />
              </Grid>
            )}
          </Grid>
          :
          <Grid container spacing={2}>
            <Grid container justifyContent="center">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/d/d1/CiteFix_logo.svg"
                alt="CiteFix logo"
              />
            </Grid>
            <Grid container justifyContent="center" sx={{ marginTop: "50px" }}>
              <Typography variant="h6">
                Please <a href="/api/login">login</a> first to use the tool.
              </Typography>
            </Grid>
          </Grid>
        }
      </Paper>
    </Container>
  )
}

export default Main;
